import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Badge } from '../../components/Badge';
import { Pagination } from '../../components/Pagination';
import { HelpTip } from '../../components/HelpTip';
import { CodigoArticulo } from '../../components/CodigoArticulo';
import { useFilasConIdentidad } from '../../lib/identidadArticulos';
import type { DetallePrecioPublicacion, EstadoComisionesMl, EstadoPrecio, FilaPrecioPublicacion, FormacionPrecio } from './types';
import { fmt, fmtCosto, fmtDiferenciaPct, fmtPorcentaje } from './formato';
import { CANDIDATOS, ComparacionCombo, FilasComponentes, FUENTE_COSTO_BASE } from './costos-comunes';
import { AYUDA_MAYORISTAS, PreciosMayoristas, textoMotivo } from './Mayoristas';
import { tonoRentabilidad } from './ui';

const PUNTO_TONO = { verde: 'bg-emerald-500', amarillo: 'bg-amber-500', rojo: 'bg-red-500' } as const;

/** Columna «Mayoristas»: precio por cantidad con un punto del color del semáforo de rentabilidad neta. */
function CeldaMayoristas({ c }: { c: FormacionPrecio }) {
  if (!c.mayoristas) return <span className="text-xs text-slate-300">—</span>; // backend anterior (durante un deploy)
  if (c.mayoristas.motivo) return <span className="text-xs text-slate-300" title={textoMotivo(c.mayoristas.motivo, c.envioGratisDesde)}>—</span>;
  return (
    <span className="flex flex-col gap-0.5 font-mono text-xs">
      {c.mayoristas.tramos.map(t => (
        <span key={t.cantidad} className="flex items-center gap-1"
          title={`Rentabilidad neta ${fmtPorcentaje(t.rentabilidadNeta.pctSobreCosto)} de lo que costó${t.pierde ? ': pierde plata' : ''}`}>
          <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${PUNTO_TONO[tonoRentabilidad(t.rentabilidadNeta.pctSobreCosto, c.rentabilidadPct ?? 0)]}`} />
          Desde {t.cantidad}: {fmt(t.precio)}
        </span>
      ))}
    </span>
  );
}

const PAGE_SIZE_PRECIOS = 50;

function tonoDiferencia(d: number | null): 'green' | 'amber' | 'red' | 'slate' {
  if (d == null) return 'slate';
  const a = Math.abs(d);
  return a < 5 ? 'green' : a < 15 ? 'amber' : 'red';
}

/** Marcas de estado del precio (spec §6), con su explicación en palabras simples. */
const MARCAS_ESTADO: Record<Exclude<EstadoPrecio, 'ok'>, { texto: string; ayuda: string; clase: string }> = {
  sin_costo: { texto: 'Sin costo', clase: 'bg-red-50 text-red-700',
    ayuda: 'No hay costo del producto: sin eso no se puede sugerir precio. Se carga en la planilla de Artículos Proveedor.' },
  config_invalida: { texto: 'Revisar parámetros', clase: 'bg-red-50 text-red-700',
    ayuda: 'La comisión, el costo fijo y los IIBB suman 100% o más: no queda nada del precio. Revisalos en la solapa Parámetros.' },
  sin_rentabilidad: { texto: 'Sin rentabilidad', clase: 'bg-amber-50 text-amber-800',
    ayuda: 'Falta la rentabilidad deseada: se carga en la planilla 02_CODIGOS, columna «% Rent. deseada».' },
  sin_peso: { texto: 'Sin peso', clase: 'bg-amber-50 text-amber-800',
    ayuda: 'El precio da {envio} o más y ahí Mercado Libre cobra el envío según el peso: sin peso no se puede calcular. Falta cargar el peso de la publicación.' },
  sin_descuento: { texto: 'Sin descuento', clase: 'bg-slate-100 text-slate-700',
    ayuda: 'No hay ningún descuento cargado (ni del proveedor, ni de la marca, ni del SKU). Si el proveedor hace descuento, el costo real puede ser menor y el precio sugerido queda alto.' },
};

function textoAlerta(c: FormacionPrecio) {
  if (!c.alerta) return null;
  const pct = Math.abs(c.alerta.variacionPct).toLocaleString('es-AR', { maximumFractionDigits: 1 });
  const texto = `${c.alerta.direccion === 'sube' ? '▲ Sube' : '▼ Baja'} ${pct}%`;
  const ayuda = `El precio sugerido se aleja del precio de Mercado Libre más de ${c.umbralAlertaPct}% (el umbral de alerta de Parámetros).`
    + (c.rentabilidadNetaActual ? ` Con el precio de hoy te queda ${fmtPorcentaje(c.rentabilidadNetaActual.pctSobreCosto)} de lo que costó.` : '');
  return { texto, ayuda };
}

/** Estado y alerta de una publicación, cada uno con su explicación al pasar el mouse. */
export function Marcas({ c }: { c: FormacionPrecio }) {
  const marca = c.estado !== 'ok' ? MARCAS_ESTADO[c.estado] : null;
  const estado = marca && { ...marca, ayuda: marca.ayuda.replace('{envio}', c.envioGratisDesde != null ? fmt(c.envioGratisDesde) : 'el precio del envío gratis') };
  const alerta = textoAlerta(c);
  if (!estado && !alerta) return <span className="text-xs text-slate-300">—</span>;
  return (
    <span className="flex flex-col items-start gap-1">
      {estado && <span title={estado.ayuda} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${estado.clase}`}>{estado.texto}</span>}
      {alerta && <span title={alerta.ayuda} className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-800">{alerta.texto}</span>}
    </span>
  );
}

/** Fila aplanada para la tabla (y para exportar a CSV/XLSX con valores legibles). */
function aplanar(f: FilaPrecioPublicacion) {
  const c = f.calculo;
  return {
    _fila: f,
    mlaId: f.mlaId,
    variante: f.varianteTexto ?? f.variantId ?? '',
    titulo: f.titulo ?? '',
    skuInterno: f.skuInterno,
    tipo: c.comision.etiqueta + (c.comision.fuente === 'tabla_por_defecto' ? ' (por defecto)' : ''),
    costoConIva: c.costoBaseConIva,
    fuenteCosto: c.fuenteCostoBase ? FUENTE_COSTO_BASE[c.fuenteCostoBase] : 'Sin costo',
    envio: c.envio.costo,
    comisionPct: c.comision.pct,
    comisionFuente: c.comision.fuenteTexto,
    cargoFijoMl: c.comision.cargoFijo,
    sumaPct: c.sumaPorcentajes,
    rentabilidadPct: c.rentabilidadPct,
    precioSugerido: c.precioSugeridoRedondeado,
    precioSinIibb: c.precioSinIibb,
    iibbProvincial: c.iibbProvincial,
    rentBruta: c.rentabilidadBruta?.monto ?? null,
    rentBrutaPct: c.rentabilidadBruta?.pctSobreCosto ?? null,
    rentNeta: c.rentabilidadNeta?.monto ?? null,
    rentNetaPct: c.rentabilidadNeta?.pctSobreCosto ?? null,
    extraContado: c.gananciaExtraContado,
    precioActual: c.precioActualMl,
    diferenciaPct: c.diferenciaPct,
    mayoristas: (c.mayoristas?.tramos ?? []).map(t => `Desde ${t.cantidad}: ${t.precio}`).join(' · '),
    marcas: [c.estado !== 'ok' ? MARCAS_ESTADO[c.estado].texto : null, textoAlerta(c)?.texto].filter(Boolean).join(' · '),
    avisos: c.avisos.map(a => a.mensaje).join(' · '),
  };
}
type FilaPlana = ReturnType<typeof aplanar>;

export function PreciosPorPublicacion() {
  const [q, setQ] = useState('');
  const qDebounced = useDebounce(q, 400);
  const [soloConAvisos, setSoloConAvisos] = useState(false);
  const [soloConAlertas, setSoloConAlertas] = useState(false);
  const [verMayoristas, setVerMayoristas] = useState(false);
  const [diferenciaMin, setDiferenciaMin] = useState('');
  const difDebounced = useDebounce(diferenciaMin, 400);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_PRECIOS);
  const [data, setData] = useState<{ data: FilaPrecioPublicacion[]; total: number }>({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  // Vigencia de la comisión real de ML (la define el backend); sólo para el texto de ayuda
  const [vigenciaDias, setVigenciaDias] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    api.get<EstadoComisionesMl>('/api/v3/precios/comisiones-ml')
      .then(e => { if (!cancelado) setVigenciaDias(e.vigenciaDias ?? null); })
      .catch(() => { /* sin dato: el texto de ayuda queda genérico */ });
    return () => { cancelado = true; };
  }, []);

  useEffect(() => { setPage(1); }, [qDebounced, soloConAvisos, soloConAlertas, difDebounced, pageSize]);

  useEffect(() => {
    let cancelado = false;
    const params = new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize) });
    if (qDebounced.trim()) params.set('q', qDebounced.trim());
    if (soloConAvisos) params.set('soloConAvisos', 'true');
    if (soloConAlertas) params.set('soloConAlertas', 'true');
    const dif = Number(difDebounced.replace(',', '.'));
    if (difDebounced.trim() && Number.isFinite(dif) && dif >= 0) params.set('diferenciaMinPct', String(dif));
    setLoading(true);
    setError(null);
    api.get<{ data: FilaPrecioPublicacion[]; total: number }>(`/api/v3/precios/publicaciones?${params}`)
      .then(r => { if (!cancelado) setData(r); })
      .catch(e => { if (!cancelado) { setData({ data: [], total: 0 }); setError(extractErrorMessage(e)); } })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [qDebounced, soloConAvisos, soloConAlertas, difDebounced, page, pageSize]);

  const filasPlanas = useMemo(() => data.data.map(aplanar), [data]);
  const filas = useFilasConIdentidad(filasPlanas, r => r.skuInterno);
  const redondeo = data.data[0]?.calculo.redondeoPrecio ?? 5;
  const montoYPct = (monto: number | null, pct: number | null) => (
    <span className="font-mono text-xs">{fmtCosto(monto)}<span className="block text-[10px] text-slate-500">{fmtPorcentaje(pct)}</span></span>
  );

  const columnas: Array<{ key: string; header: string; render?: (r: FilaPlana) => ReactNode; align?: 'left' | 'right' | 'center'; help?: string; width?: string }> = [
    { key: 'mlaId', header: 'MLA', render: r => <span className="font-mono text-xs">{r.mlaId}</span> },
    { key: 'variante', header: 'Variante', render: r => <span className="text-xs text-slate-600">{r.variante || '—'}</span> },
    { key: 'titulo', header: 'Título', width: '16rem', render: r => <span className="block truncate text-xs" title={r.titulo}>{r.titulo || '—'}</span> },
    { key: 'codigoArticulo', header: 'SKU', render: r => <span className="text-xs"><CodigoArticulo sku={r.skuInterno} />{r._fila.esCombo && <span className="ml-1 text-[10px] text-brand-600">combo</span>}</span> },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'tipo', header: 'Tipo', help: 'Tipo de publicación de ML (define la comisión)', render: r => <span className="text-xs">{r.tipo}</span> },
    { key: 'costoConIva', header: 'Costo c/IVA', align: 'right', help: 'Costo base con IVA y su fuente', render: r => (
      <span className="font-mono text-xs" title={r.fuenteCosto}>{fmtCosto(r.costoConIva)}<span className="block text-[10px] text-slate-400">{r.fuenteCosto}</span></span>
    ) },
    { key: 'envio', header: 'Envío', align: 'right', render: r => <span className="font-mono text-xs">{fmtCosto(r.envio)}</span> },
    { key: 'comisionPct', header: 'Comisión', align: 'right', help: 'Comisión de ML y su fuente: ML (fecha de consulta) o tabla (estimada)', render: r => (
      <span className="font-mono text-xs">
        {fmtPorcentaje(r.comisionPct)}
        {r.cargoFijoMl > 0 && <span className="block text-[10px] text-slate-500">+ {fmtCosto(r.cargoFijoMl)} fijo</span>}
        <span className={`block text-[10px] ${r._fila.calculo.comision.fuente === 'ml' ? 'text-emerald-600' : 'text-amber-600'}`}>{r.comisionFuente}</span>
      </span>
    ) },
    { key: 'sumaPct', header: 'Suma %', align: 'right', help: 'Lo que se descuenta del precio: comisión + costo fijo + IIBB SIRTAC + IIBB Tucumán + IIBB provincial', render: r => <span className="font-mono text-xs">{fmtPorcentaje(r.sumaPct)}</span> },
    { key: 'rentabilidadPct', header: 'Rent.', align: 'right', help: 'Rentabilidad deseada, como % de lo que te costó el producto (02_CODIGOS)', render: r => <span className="font-mono text-xs">{fmtPorcentaje(r.rentabilidadPct)}</span> },
    { key: 'precioSugerido', header: 'Sugerido', align: 'right', help: `Precio sugerido, redondeado siempre para arriba al múltiplo de $${redondeo}`, render: r => <span className="font-mono text-xs font-semibold text-slate-800">{fmt(r.precioSugerido)}</span> },
    { key: 'precioSinIibb', header: 'Sin IIBB', align: 'right', help: 'El precio sugerido sin el IIBB provincial, y abajo el IIBB aparte (para la Factura A). Sumados dan el precio sugerido. La cuenta (precio − precio × IIBB) está a confirmar con el contador.', render: r => (
      <span className="font-mono text-xs">{fmtCosto(r.precioSinIibb)}<span className="block text-[10px] text-slate-500">IIBB {fmtCosto(r.iibbProvincial)}</span></span>
    ) },
    { key: 'rentBruta', header: 'Rent. bruta', align: 'right', help: 'Lo que queda después de pagar a Mercado Libre (comisión, cargo fijo y envío), lo que te costó, el costo fijo y los IIBB SIRTAC y Tucumán, antes del IIBB provincial. El % es sobre lo que te costó.', render: r => montoYPct(r.rentBruta, r.rentBrutaPct) },
    { key: 'rentNeta', header: 'Rent. neta', align: 'right', help: 'Lo que te queda en el bolsillo al precio sugerido, después de la comisión y el cargo de Mercado Libre, el envío, el costo fijo y los IIBB. El % es sobre lo que te costó. No descuenta el impuesto a las Ganancias.', render: r => montoYPct(r.rentNeta, r.rentNetaPct) },
    { key: 'extraContado', header: 'Extra contado', align: 'right', help: 'Lo que ganás de más si le pagás contado al proveedor (su descuento por pago contado). No se usa para el precio.', render: r => <span className="font-mono text-xs">{r.extraContado == null ? '—' : fmtCosto(r.extraContado)}</span> },
    ...(verMayoristas ? [{ key: 'mayoristas', header: 'Mayoristas', help: AYUDA_MAYORISTAS, render: (r: FilaPlana) => <CeldaMayoristas c={r._fila.calculo} /> }] : []),
    { key: 'precioActual', header: 'Actual ML', align: 'right', render: r => <span className="font-mono text-xs">{fmt(r.precioActual)}</span> },
    { key: 'diferenciaPct', header: 'Dif. %', align: 'right', help: '(Sugerido − actual ML) / actual ML', render: r => (
      r.diferenciaPct == null ? <span className="text-xs text-slate-400">—</span> : <Badge tone={tonoDiferencia(r.diferenciaPct)}>{fmtDiferenciaPct(r.diferenciaPct)}</Badge>
    ) },
    { key: 'marcas', header: 'Marcas', help: 'Datos que faltan para el precio (se filtran con «Sólo con avisos o datos que faltan») y alerta cuando el precio de Mercado Libre se aleja del sugerido («Sólo con alerta de precio»). Pasá el mouse por cada marca para ver qué quiere decir.', render: r => <Marcas c={r._fila.calculo} /> },
    { key: 'avisos', header: 'Avisos', align: 'center', render: r => (
      r._fila.calculo.avisos.length === 0
        ? <span className="text-xs text-slate-300">—</span>
        : <span title={r.avisos} className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">⚠ {r._fila.calculo.avisos.length}</span>
    ) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <strong>Sugerencia: no se envía a MercadoLibre.</strong>
        <span>Los precios de las publicaciones se cambian a mano. Tocá una fila para ver cómo se forma el precio.</span>
        <HelpTip tips={[
          'Precio sugerido = (costo base con IVA × (1 + rentabilidad) + envío + cargo fijo ML) / (1 − comisión − costo fijo − IIBB SIRTAC − IIBB Tucumán − IIBB provincial).',
          'La rentabilidad es sobre lo que te costó: si te costó $100 y la rentabilidad es 20%, te quedan $20 limpios después de pagar a Mercado Libre, el costo fijo y los IIBB.',
          `La comisión es la real de ML por categoría y tipo de publicación ("ML (dd/mm)", ${vigenciaDias != null ? `consultada hace ${vigenciaDias} días o menos` : 'consultada recientemente'}); si no hay dato, la de la tabla ("tabla", estimada).`,
          'El costo base es el mismo de la vista previa de costos (lista con descuentos ERP, BOM o planilla COMBO).',
          'El rango de peso y la rentabilidad deseada vienen de la planilla 02_CODIGOS (columnas "PESO ML" y "% Rent. deseada").',
          'La comisión, el costo fijo, los IIBB, el redondeo y las tarifas de envío se configuran en la solapa Parámetros.',
        ]} />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="precios-q" className="text-xs font-medium uppercase tracking-wide text-slate-500">Buscar</label>
          <input id="precios-q" value={q} onChange={e => setQ(e.target.value)} placeholder="MLA, SKU o título"
            className="w-64 rounded border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="precios-dif" className="text-xs font-medium uppercase tracking-wide text-slate-500">Diferencia mayor a (%)</label>
          <input id="precios-dif" value={diferenciaMin} onChange={e => setDiferenciaMin(e.target.value)} inputMode="decimal" placeholder="p. ej. 10"
            className="w-36 rounded border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:border-brand-400 focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-700"
          title="Publicaciones a las que les falta un dato o tienen algo para revisar: los avisos (⚠) y las marcas Sin costo, Sin rentabilidad, Revisar parámetros, Sin peso y Sin descuento de la columna «Marcas».">
          <input type="checkbox" checked={soloConAvisos} onChange={e => setSoloConAvisos(e.target.checked)} />
          Sólo con avisos o datos que faltan
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-700"
          title="Publicaciones cuyo precio en Mercado Libre se aleja del sugerido más que el umbral de alerta de Parámetros (▲ Sube / ▼ Baja en «Marcas»).">
          <input type="checkbox" checked={soloConAlertas} onChange={e => setSoloConAlertas(e.target.checked)} />
          Sólo con alerta de precio
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-slate-700" title={AYUDA_MAYORISTAS}>
          <input type="checkbox" checked={verMayoristas} onChange={e => setVerMayoristas(e.target.checked)} />
          Ver precios mayoristas
        </label>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <DataTable<FilaPlana>
        columns={columnas}
        data={filas}
        loading={loading}
        onRowClick={r => setDetalleId(r._fila.id)}
        emptyMessage="No hay publicaciones con esos filtros"
        title="Precios-sugeridos-por-publicacion"
      />
      <Pagination page={page} pageSize={pageSize} total={data.total} onChange={setPage} onPageSizeChange={setPageSize} />

      {detalleId != null && <DetallePrecioModal id={detalleId} onClose={() => setDetalleId(null)} />}
    </div>
  );
}

/** Una línea del desglose: paso, fuente/detalle y valor. */
function LineaPrecio({ concepto, fuente, valor, destacado = false, informativo = false, sangria = false }: {
  concepto: string; fuente?: ReactNode; valor: ReactNode; destacado?: boolean; informativo?: boolean; sangria?: boolean;
}) {
  return (
    <tr className={`border-t border-slate-100 ${destacado ? 'bg-brand-50 font-semibold' : ''} ${informativo ? 'text-slate-400' : ''}`}>
      <td className={`px-3 py-2 text-sm ${sangria ? 'pl-8' : ''}`}>{concepto}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{fuente}</td>
      <td className="px-3 py-2 text-right font-mono text-sm whitespace-nowrap">{valor}</td>
    </tr>
  );
}

function DetallePrecioModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [d, setD] = useState<DetallePrecioPublicacion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setD(null);
    setError(null);
    api.get<DetallePrecioPublicacion>(`/api/v3/precios/publicaciones/${id}`)
      .then(r => { if (!cancelado) setD(r); })
      .catch(e => { if (!cancelado) setError(extractErrorMessage(e)); });
    return () => { cancelado = true; };
  }, [id]);

  return (
    <Modal open onClose={onClose} title="Cómo se forma el precio" size="xl">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!d && !error && <p className="text-sm text-slate-400">Cargando…</p>}
      {d && <DesglosePrecio d={d} />}
    </Modal>
  );
}

function DesglosePrecio({ d }: { d: DetallePrecioPublicacion }) {
  const c = d.calculo;
  const k = d.costos;
  const precio = c.precioSugerido;
  const monto = (pct: number | null) => (precio != null && pct != null ? fmtCosto((precio * pct) / 100) : '—');

  // Cascada de descuentos del ERP sobre la lista (sólo SKU simples con oferta).
  const lista = k.costos.listaProveedor.sinIva;
  const desc = k.descuentosErp;
  const trasProveedor = lista != null && desc ? lista * (1 - desc.proveedor / 100) : null;
  const trasMarca = trasProveedor != null && desc ? trasProveedor * (1 - desc.marca / 100) : null;
  const trasSku = trasMarca != null && desc ? trasMarca * (1 - desc.sku / 100) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="font-mono font-semibold text-slate-800">{d.mlaId}{d.variantId ? ` / ${d.variantId}` : ''}</span>
        {d.varianteTexto && <span className="text-slate-600">{d.varianteTexto}</span>}
        <span className="text-slate-600">{d.titulo ?? '—'}</span>
        <span className="text-slate-500">SKU <CodigoArticulo sku={d.skuInterno} /></span>
        {d.esCombo && <Badge tone="brand">Combo ({k.componentes.length} componentes)</Badge>}
        {d.status && <Badge tone={d.status === 'active' ? 'green' : 'slate'}>{d.status}</Badge>}
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
        <strong>Sugerencia: no se envía a MercadoLibre.</strong> El precio de la publicación se cambia a mano.
      </div>

      {(c.estado !== 'ok' || c.alerta) && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">Marcas: <Marcas c={c} /></div>
      )}

      {c.avisos.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {c.avisos.map(a => <li key={a.codigo}>⚠ {a.mensaje}</li>)}
        </ul>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Paso</th><th className="px-3 py-2">Fuente / detalle</th><th className="px-3 py-2 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {!k.esCombo && (
              <>
                <LineaPrecio concepto="Lista proveedor (sin IVA)" valor={fmtCosto(lista)}
                  fuente={k.proveedorUsado
                    ? `${k.proveedorUsado.proveedor} (${k.proveedorUsado.codigoProveedor}) — ${k.proveedorUsado.criterio === 'proveedor_estandar' ? 'proveedor estándar' : 'menor precio'}`
                    : 'Sin oferta activa con precio'} />
                <LineaPrecio sangria concepto={`Desc. proveedor ${desc ? fmtPorcentaje(desc.proveedor) : '—'}`} valor={fmtCosto(trasProveedor)} fuente="Descuento del proveedor" />
                <LineaPrecio sangria concepto={`Desc. marca ${desc ? fmtPorcentaje(desc.marca) : '—'}`} valor={fmtCosto(trasMarca)} fuente="Descuento de la marca del SKU" />
                <LineaPrecio sangria concepto={`Desc. SKU ${desc ? fmtPorcentaje(desc.sku) : '—'}`} valor={fmtCosto(trasSku)} fuente="Descuento del SKU (catálogo)" />
                <LineaPrecio sangria informativo concepto={`Contado ${desc ? fmtPorcentaje(desc.contado) : '—'} (informativo)`}
                  valor={fmtCosto(k.costos.listaConDescuentosErpContado.sinIva)} fuente="No entra en el costo base" />
              </>
            )}
            <LineaPrecio destacado concepto="Costo base (sin IVA)" valor={fmtCosto(c.costoBaseSinIva)}
              fuente={c.fuenteCostoBase ? FUENTE_COSTO_BASE[c.fuenteCostoBase] : 'Sin costo base'} />
            <LineaPrecio concepto={`+ IVA ${fmtPorcentaje(c.alicuotaIva)}`} fuente="Alícuota del SKU"
              valor={c.costoBaseConIva != null && c.costoBaseSinIva != null ? fmtCosto(c.costoBaseConIva - c.costoBaseSinIva) : '—'} />
            <LineaPrecio destacado concepto="Costo con IVA" valor={fmtCosto(c.costoBaseConIva)} />
            <LineaPrecio concepto={`+ Rentabilidad ${fmtPorcentaje(c.rentabilidadPct)} de lo que costó`} valor={fmtCosto(c.gananciaEstimada)}
              fuente='02_CODIGOS "% Rent. deseada": lo que querés que te quede limpio' />
            <LineaPrecio concepto="+ Envío" valor={fmtCosto(c.envio.costo)}
              fuente={c.envio.fuente === 'tabla_ml'
                ? (c.tramoMl && c.envio.costo > 0
                  ? `Tabla de costos de ML: envío gratis desde $33.000, tramo ${c.tramoMl.bandaTexto}, peso desde ${c.tramoMl.kgDesde} kg`
                  : 'Tabla de costos de ML: debajo de $33.000 el envío lo paga el comprador')
                : c.envio.fuente === 'tarifa' ? `Tarifa "${c.envio.rango}"` : c.envio.fuente === 'sin_tarifa' ? `"${c.envio.rango}": sin tarifa de envío` : 'Sin rango de peso (PESO ML)'} />
            <LineaPrecio concepto={`+ Cargo fijo ML ${fmtCosto(c.comision.cargoFijo)}`} valor={fmtCosto(c.comision.cargoFijo)}
              fuente={c.tramoMl
                ? `Tabla de costos de ML (vale desde el ${c.tramoMl.vigenteDesde.split('-').reverse().join('/')}): tramo ${c.tramoMl.bandaTexto}, peso desde ${c.tramoMl.kgDesde} kg`
                  + (c.tramoMl.iteraciones > 1 ? `, después de ${c.tramoMl.iteraciones} vueltas` : '')
                : c.comision.ml
                  ? `${c.comision.fuenteTexto}: por unidad, consultado a ${fmt(c.comision.ml.precioConsulta)}`
                  : 'Sin dato de ML: $0'} />
            {c.tramoMl?.cargoFijoMlReal != null && (
              <LineaPrecio sangria informativo concepto="Cargo fijo que informó ML" valor={fmtCosto(c.tramoMl.cargoFijoMlReal)}
                fuente={`${c.comision.fuenteTexto}, a ${fmt(c.comision.ml?.precioConsulta)}: sólo para comparar, se usa la tabla`} />
            )}
            <LineaPrecio concepto={`Comisión ML ${fmtPorcentaje(c.comision.pct)}`} valor={monto(c.comision.pct)}
              fuente={`${c.comision.etiqueta} (${c.comision.listingTypeId ?? 'sin tipo'}) — ${
                c.comision.fuente === 'ml'
                  ? `${c.comision.fuenteTexto}, categoría ${c.comision.ml?.categoryId ?? '—'}`
                  : c.comision.fuente === 'tabla'
                    ? 'tabla por tipo de publicación (estimada)'
                    : 'tipo desconocido: Premium por defecto (estimada)'}`} />
            <LineaPrecio concepto={`Costo fijo ${fmtPorcentaje(c.costoFijoPct)}`} valor={monto(c.costoFijoPct)} fuente="Parámetros" />
            <LineaPrecio concepto={`IIBB SIRTAC ${fmtPorcentaje(c.iibbSirtacPct)}`} valor={monto(c.iibbSirtacPct)} fuente="Parámetros" />
            <LineaPrecio concepto={`IIBB Tucumán ${fmtPorcentaje(c.iibbTucumanPct)}`} valor={monto(c.iibbTucumanPct)} fuente="Parámetros" />
            <LineaPrecio concepto={`IIBB provincial ${fmtPorcentaje(c.iibbProvincialPct)}`} valor={monto(c.iibbProvincialPct)} fuente="Parámetros" />
            <LineaPrecio concepto="Suma de porcentajes" valor={fmtPorcentaje(c.sumaPorcentajes)} fuente="Lo que se descuenta del precio" />
            <LineaPrecio concepto="Lo que queda de cada $1 de precio" valor={c.divisor != null ? `$${c.divisor.toLocaleString('es-AR', { maximumFractionDigits: 4 })}` : '—'}
              fuente="Lo que no se llevan la comisión, el costo fijo y los IIBB (1 − suma de porcentajes)" />
            <LineaPrecio destacado concepto="Precio sugerido" valor={fmtCosto(c.precioSugerido)}
              fuente="Lo que tiene que quedar (costo con IVA + rentabilidad + envío + cargo fijo ML) dividido por lo que queda de cada $1" />
            <LineaPrecio destacado concepto="Redondeado" valor={fmt(c.precioSugeridoRedondeado)} fuente={`Siempre para arriba, al múltiplo de $${c.redondeoPrecio}`} />
            <LineaPrecio sangria concepto="Precio sin IIBB provincial" valor={fmtCosto(c.precioSinIibb)} fuente="Para la Factura A (cuenta a confirmar con el contador)" />
            <LineaPrecio sangria concepto={`IIBB provincial ${fmtPorcentaje(c.iibbProvincialPct)}`} valor={fmtCosto(c.iibbProvincial)} fuente="Sumado al anterior da el redondeado" />
            <LineaPrecio concepto="Precio actual ML" valor={fmt(c.precioActualMl)} fuente={d.publicacionMlEncontrada ? 'Publicación sincronizada de ML' : 'Publicación no sincronizada'} />
            <LineaPrecio concepto="Diferencia" valor={fmtDiferenciaPct(c.diferenciaPct)} fuente="(Redondeado − actual) / actual" />
            <LineaPrecio concepto="Rentabilidad bruta" valor={fmtCosto(c.rentabilidadBruta?.monto)}
              fuente={`Después de Mercado Libre, el costo, el costo fijo y los IIBB SIRTAC y Tucumán; antes del IIBB provincial (${fmtPorcentaje(c.rentabilidadBruta?.pctSobreCosto)} de lo que costó)`} />
            <LineaPrecio destacado concepto="Rentabilidad neta" valor={fmtCosto(c.rentabilidadNeta?.monto)}
              fuente={`Lo que te queda en el bolsillo al redondeado (${fmtPorcentaje(c.rentabilidadNeta?.pctSobreCosto)} de lo que costó). No descuenta Ganancias`} />
            <LineaPrecio concepto="Ganancia extra si pagás contado" valor={c.gananciaExtraContado == null ? '—' : fmtCosto(c.gananciaExtraContado)}
              fuente="Descuento por pago contado del proveedor; no se usa para el precio" />
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500" title={AYUDA_MAYORISTAS}>Precios mayoristas</h3>
        <PreciosMayoristas c={c} />
      </div>

      {(k.esCombo || k.costoPlanillaCombo) && <ComparacionCombo data={k} />}

      {k.esCombo && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Componentes (costo unitario sin IVA)</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">SKU</th><th className="px-3 py-2 text-right">Cant.</th>
                {CANDIDATOS.map(x => <th key={x.id} className="px-3 py-2 text-right">{x.label}</th>)}
                <th className="px-3 py-2">Proveedor</th>
              </tr>
            </thead>
            <tbody><FilasComponentes c={k} /></tbody>
          </table>
        </div>
      )}
      {k.motivo && <p className="text-sm text-red-600">Costo: {k.motivo}</p>}
    </div>
  );
}
